import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Grid,
  InputAdornment,
  Alert
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import BedrockModelBrowser from './BedrockModelBrowser';

const ApprovedModelForm = ({ model, onSave, onCancel }) => {
  const isEditMode = !!model;
  const [formData, setFormData] = useState({
    name: '', provider: '', modelId: '', defaultMaxTokens: '', maxTokensLimit: '',
    recommendedTemperature: '0.0', costPer1MInputTokens: '', costPer1MOutputTokens: '',
    description: '', supportsVision: false, displayOrder: '0'
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [showPreFillMessage, setShowPreFillMessage] = useState(false);

  useEffect(() => {
    if (model) {
      setFormData({
        name: model.name || '', provider: model.provider || '', modelId: model.modelId || '',
        defaultMaxTokens: model.defaultMaxTokens?.toString() || '',
        maxTokensLimit: model.maxTokensLimit?.toString() || '',
        recommendedTemperature: model.recommendedTemperature?.toString() || '0.0',
        costPer1MInputTokens: model.costPer1MInputTokens?.toString() || '',
        costPer1MOutputTokens: model.costPer1MOutputTokens?.toString() || '',
        description: model.description || '', supportsVision: model.supportsVision || false,
        displayOrder: model.displayOrder?.toString() || '0'
      });
    }
  }, [model]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.provider.trim()) errors.provider = 'Provider is required';
    if (!formData.modelId.trim()) errors.modelId = 'Model ID is required';
    if (!formData.maxTokensLimit) errors.maxTokensLimit = 'Max Tokens Limit is required';
    if (!formData.recommendedTemperature) errors.recommendedTemperature = 'Recommended Temperature is required';

    const temp = parseFloat(formData.recommendedTemperature);
    if (isNaN(temp) || temp < 0.0 || temp > 1.0) {
      errors.recommendedTemperature = 'Temperature must be between 0.0 and 1.0';
    }

    const maxTokensLimit = parseInt(formData.maxTokensLimit, 10);
    if (isNaN(maxTokensLimit) || maxTokensLimit <= 0) {
      errors.maxTokensLimit = 'Max Tokens Limit must be a positive integer';
    }

    if (formData.defaultMaxTokens) {
      const defaultMaxTokens = parseInt(formData.defaultMaxTokens, 10);
      if (isNaN(defaultMaxTokens) || defaultMaxTokens <= 0) {
        errors.defaultMaxTokens = 'Default Max Tokens must be a positive integer';
      } else if (defaultMaxTokens > maxTokensLimit) {
        errors.defaultMaxTokens = 'Default Max Tokens cannot exceed Max Tokens Limit';
      }
    }

    const displayOrder = parseInt(formData.displayOrder, 10);
    if (isNaN(displayOrder)) errors.displayOrder = 'Display Order must be a number';

    if (formData.costPer1MInputTokens.trim() && isNaN(parseFloat(formData.costPer1MInputTokens.trim()))) {
      errors.costPer1MInputTokens = 'Cost must be a valid number';
    }
    if (formData.costPer1MOutputTokens.trim() && isNaN(parseFloat(formData.costPer1MOutputTokens.trim()))) {
      errors.costPer1MOutputTokens = 'Cost must be a valid number';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      await onSave({
        name: formData.name.trim(),
        provider: formData.provider.trim(),
        modelId: formData.modelId.trim(),
        defaultMaxTokens: formData.defaultMaxTokens ? parseInt(formData.defaultMaxTokens, 10) : null,
        maxTokensLimit: parseInt(formData.maxTokensLimit, 10),
        recommendedTemperature: parseFloat(formData.recommendedTemperature),
        costPer1MInputTokens: formData.costPer1MInputTokens.trim() ? parseFloat(formData.costPer1MInputTokens.trim()) : null,
        costPer1MOutputTokens: formData.costPer1MOutputTokens.trim() ? parseFloat(formData.costPer1MOutputTokens.trim()) : null,
        description: formData.description.trim() || null,
        supportsVision: formData.supportsVision,
        displayOrder: parseInt(formData.displayOrder, 10)
      });
    } finally {
      setSaving(false);
    }
  };

  const handleBrowseModels = () => {
    setBrowserOpen(true);
  };

  const handleSelectModel = (selectedModel) => {
    // Pre-fill form fields from AWS Bedrock model data
    setFormData(prev => ({
      ...prev,
      modelId: selectedModel.modelId || '',
      name: selectedModel.modelName || '',
      provider: selectedModel.providerName || '',
      supportsVision: selectedModel.inputModalities?.includes('IMAGE') || false
    }));
    
    // Show info message about manual fields
    setShowPreFillMessage(true);
    setBrowserOpen(false);
    
    // Clear validation errors for pre-filled fields
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.modelId;
      delete newErrors.name;
      delete newErrors.provider;
      return newErrors;
    });
  };

  const fields = [
    { name: 'name', label: 'Name', required: true, xs: 6 },
    { name: 'provider', label: 'Provider', required: true, xs: 6 },
    { name: 'modelId', label: 'Model ID', required: true, xs: 12, disabled: isEditMode, 
      helperText: 'AWS Bedrock model identifier' },
    { name: 'defaultMaxTokens', label: 'Default Max Tokens', type: 'number', xs: 6, 
      helperText: 'Optional: Recommended max tokens', inputProps: { min: 1 } },
    { name: 'maxTokensLimit', label: 'Max Tokens Limit', type: 'number', required: true, xs: 6, 
      inputProps: { min: 1 } },
    { name: 'recommendedTemperature', label: 'Recommended Temperature', type: 'number', required: true, xs: 6,
      helperText: 'Range: 0.0 to 1.0', inputProps: { min: 0, max: 1, step: 0.1 } },
    { name: 'displayOrder', label: 'Display Order', type: 'number', xs: 6,
      helperText: 'Lower numbers appear first', inputProps: { min: 0 } },
    { name: 'costPer1MInputTokens', label: 'Cost Per 1M Input Tokens', xs: 6,
      helperText: 'Optional: e.g., $3.00', startAdornment: '$' },
    { name: 'costPer1MOutputTokens', label: 'Cost Per 1M Output Tokens', xs: 6,
      helperText: 'Optional: e.g., $15.00', startAdornment: '$' },
    { name: 'description', label: 'Description', xs: 12, multiline: true, rows: 3,
      helperText: 'Optional: Additional information about this model' }
  ];

  return (
    <Box>
      {showPreFillMessage && (
        <Alert severity="info" onClose={() => setShowPreFillMessage(false)} sx={{ mb: 2 }}>
          Model details pre-filled from AWS Bedrock. Please enter pricing, token limits, and recommended temperature.
        </Alert>
      )}
      
      {!isEditMode && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={handleBrowseModels}
            fullWidth
          >
            Browse Bedrock Models
          </Button>
        </Box>
      )}

      <Grid container spacing={2}>
        {fields.map(field => (
          <Grid item xs={12} sm={field.xs} key={field.name}>
            <TextField
              label={field.label}
              type={field.type}
              value={formData[field.name]}
              onChange={handleChange(field.name)}
              error={!!validationErrors[field.name]}
              helperText={validationErrors[field.name] || field.helperText}
              fullWidth
              required={field.required}
              disabled={field.disabled}
              multiline={field.multiline}
              rows={field.rows}
              inputProps={field.inputProps}
              InputProps={field.startAdornment ? {
                startAdornment: <InputAdornment position="start">{field.startAdornment}</InputAdornment>
              } : undefined}
            />
          </Grid>
        ))}
        <Grid item xs={12}>
          <FormControlLabel
            control={<Checkbox checked={formData.supportsVision} onChange={handleChange('supportsVision')} />}
            label="Supports Vision (image processing)"
          />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button variant="outlined" startIcon={<CancelIcon />} onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      <BedrockModelBrowser
        open={browserOpen}
        onSelectModel={handleSelectModel}
        onClose={() => setBrowserOpen(false)}
      />
    </Box>
  );
};

ApprovedModelForm.propTypes = {
  model: PropTypes.shape({
    id: PropTypes.string,
    modelId: PropTypes.string,
    name: PropTypes.string,
    provider: PropTypes.string,
    defaultMaxTokens: PropTypes.number,
    maxTokensLimit: PropTypes.number,
    recommendedTemperature: PropTypes.number,
    costPer1MInputTokens: PropTypes.string,
    costPer1MOutputTokens: PropTypes.string,
    description: PropTypes.string,
    supportsVision: PropTypes.bool,
    displayOrder: PropTypes.number
  }),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default ApprovedModelForm;
